import React, { useEffect, useState } from 'react';
import { betaCodesList, betaCodesCreate, betaCodesRevoke, type BetaCodeRow } from '../services/apiService';

function randomCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no O/0/I/1 — easy to read aloud
  let s = '';
  for (let i = 0; i < 6; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return `BETA-${s}`;
}

const GlobalBetaCodes: React.FC = () => {
  const [items, setItems] = useState<BetaCodeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [code, setCode] = useState(randomCode());
  const [maxUses, setMaxUses] = useState(1);
  const [note, setNote] = useState('');
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr('');
    try {
      setItems(await betaCodesList());
    } catch (e: any) {
      setErr(e?.message || 'Failed to load beta codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!code.trim()) {
      setErr('Code is required');
      return;
    }
    setCreating(true);
    setErr('');
    try {
      await betaCodesCreate({ code: code.trim(), max_uses: maxUses, note: note.trim() || undefined });
      setCode(randomCode());
      setMaxUses(1);
      setNote('');
      await load();
    } catch (e: any) {
      setErr(e?.message || 'Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (row: BetaCodeRow) => {
    setErr('');
    try {
      await betaCodesRevoke(row.id);
      await load();
    } catch (e: any) {
      setErr(e?.message || 'Failed to revoke code');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-100">Beta access codes</h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1 text-sm">
          Signup requires one of these codes while AntNest is in beta. Each code can be used up to its
          "max uses" before it stops working; revoke a code to disable it immediately.
        </p>
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
          {err}
        </div>
      )}

      <div className="border border-dashboard-border dark:border-gray-700 rounded-xl p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Code</label>
          <input
            className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 rounded px-2 py-1.5 text-sm font-mono"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Max uses</label>
          <input
            type="number"
            min={1}
            className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 rounded px-2 py-1.5 text-sm w-20"
            value={maxUses}
            onChange={(e) => setMaxUses(Math.max(1, Number(e.target.value) || 1))}
          />
        </div>
        <div className="flex-1 min-w-[160px]">
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Note (optional)</label>
          <input
            className="w-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 rounded px-2 py-1.5 text-sm"
            placeholder="e.g. Friends & family batch 1"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <button type="button" className="btn btn-primary" onClick={create} disabled={creating}>
          {creating ? 'Creating…' : 'Create code'}
        </button>
      </div>

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-dashboard-border dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="text-left p-3">Code</th>
                <th className="text-left p-3">Note</th>
                <th className="text-left p-3">Uses</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Created</th>
                <th className="text-left p-3">Redeemed by</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-t border-gray-200 dark:border-gray-700">
                  <td className="p-3 font-mono text-xs">{row.code}</td>
                  <td className="p-3">{row.note || <span className="text-gray-400">—</span>}</td>
                  <td className="p-3">
                    {row.uses_count} / {row.max_uses}
                  </td>
                  <td className="p-3">
                    {row.is_active ? (
                      <span className="px-1.5 py-0.5 rounded bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 text-xs font-medium">
                        Active
                      </span>
                    ) : (
                      <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs font-medium">
                        Revoked
                      </span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-gray-500 dark:text-gray-400">
                    {new Date(row.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-3 text-xs">
                    {row.beta_code_redemptions.length > 0 ? (
                      <span title={row.beta_code_redemptions.map((r) => r.email).join(', ')}>
                        {row.beta_code_redemptions.map((r) => r.email).join(', ')}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="p-3 text-right">
                    {row.is_active && (
                      <button type="button" className="btn btn-warning btn-small" onClick={() => revoke(row)}>
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-gray-400 italic">
                    No beta codes yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GlobalBetaCodes;
